import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaMapMarkerAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaUsers,
  FaRegClock,
  FaArrowLeft,
  FaPlusCircle,
} from "react-icons/fa";
import Button from "../../Components/UI/Button";
import { Card, Badge } from "../../Components/UI/Card";
import { StarDisplay } from "../../Components/Marketplace/StarRating";
import BookingModal from "../../Components/Marketplace/BookingModal";
import {
  getPackageById,
  getPartnerReviews,
  purchasePackage,
  getApiErrorMessage,
} from "../../Components/Marketplace/services/marketplaceApi";
import { openMarketplaceCheckout } from "../../Components/Marketplace/services/marketplaceCheckout";
import { formatINR } from "../../Components/Marketplace/marketplaceUtils";

const PLACEHOLDER_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='480'><rect width='100%' height='100%' fill='%23f6f7f9'/><text x='50%' y='50%' font-family='sans-serif' font-size='20' fill='%238993a4' text-anchor='middle' dominant-baseline='middle'>Rasutrip</text></svg>`
  );

const PackageDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pkg, setPkg] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPackageById(id);
      setPkg(res?.data || null);

      const partnerId = res?.data?.partnerId?._id;
      if (partnerId) {
        try {
          const rRes = await getPartnerReviews(partnerId);
          setReviews(Array.isArray(rRes?.data) ? rRes.data : []);
        } catch {
          setReviews([]);
        }
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load this package."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    window.scrollTo(0, 0);
  }, [load]);

  const handleBuyNowClick = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.info("Please login to buy this package.");
      navigate("/login/customer");
      return;
    }
    setModalError("");
    setShowModal(true);
  };

  const handleSubmitPurchase = async (payload) => {
    setSubmitting(true);
    setModalError("");
    try {
      const res = await purchasePackage(id, payload);
      const { order } = res?.data || {};

      await openMarketplaceCheckout({
        order,
        description: pkg?.title,
        onSuccess: () => {
          toast.success("Payment successful! Your package is booked.");
          setShowModal(false);
          setSubmitting(false);
          navigate("/dashboard?tab=bookings&sub=Marketplace");
        },
        onError: (err) => {
          setSubmitting(false);
          toast.error(getApiErrorMessage(err, "Payment could not be verified. Please contact support."));
        },
        onDismiss: () => {
          setSubmitting(false);
        },
      });
    } catch (err) {
      setSubmitting(false);
      setModalError(getApiErrorMessage(err, "Could not start the purchase. Please try again."));
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1100px] mx-auto px-4 py-16 text-center text-ink-500">
        Loading package...
      </div>
    );
  }

  if (error || !pkg) {
    return (
      <div className="max-w-[1100px] mx-auto px-4 py-16 text-center">
        <p className="text-red-600 font-semibold mb-3">{error || "Package not found."}</p>
        <Link to="/marketplace" className="text-brand-600 font-semibold">
          ← Back to Marketplace
        </Link>
      </div>
    );
  }

  const photos = (pkg.images || []).length ? pkg.images : [PLACEHOLDER_IMG];
  const partner = pkg.partnerId || {};

  return (
    <div className="min-h-screen bg-ink-50/40">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate("/marketplace")}
          className="inline-flex items-center gap-2 text-sm font-semibold text-ink-600 hover:text-brand-600 mb-4"
        >
          <FaArrowLeft size={12} /> Back to Marketplace
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="overflow-hidden mb-6">
              <img
                src={photos[activeImg] || PLACEHOLDER_IMG}
                alt={pkg.title}
                className="w-full h-72 sm:h-96 object-cover"
                onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
              />
              {photos.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {photos.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`shrink-0 h-16 w-24 rounded-lg overflow-hidden border-2 ${
                        i === activeImg ? "border-brand-600" : "border-transparent"
                      }`}
                    >
                      <img src={p} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </Card>

            <div className="mb-2 flex flex-wrap items-center gap-2">
              {pkg.durationDays && <Badge tone="dark">{pkg.durationDays} Days</Badge>}
              {pkg.maxTravellers && (
                <Badge tone="ink">
                  <FaUsers className="inline mr-1" /> Up to {pkg.maxTravellers} travellers
                </Badge>
              )}
            </div>
            <h1 className="text-2xl font-extrabold text-ink-900 mb-2">{pkg.title}</h1>
            {!!(pkg.destinationCities || []).length && (
              <p className="flex items-center gap-1.5 text-ink-500 text-sm mb-4">
                <FaMapMarkerAlt /> {pkg.destinationCities.join(", ")}
              </p>
            )}
            <p className="text-ink-700 leading-relaxed whitespace-pre-line mb-6">{pkg.description}</p>

            {!!(pkg.itinerary || []).length && (
              <Card className="p-5 mb-6">
                <h2 className="font-bold text-ink-900 mb-4 flex items-center gap-2">
                  <FaRegClock className="text-brand-600" /> Day-by-day itinerary
                </h2>
                <div className="space-y-4">
                  {pkg.itinerary
                    .slice()
                    .sort((a, b) => (a.day || 0) - (b.day || 0))
                    .map((it, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="shrink-0 h-8 w-8 rounded-full bg-brand-50 text-brand-700 font-bold text-sm flex items-center justify-center">
                          {it.day}
                        </div>
                        <p className="text-sm text-ink-700 pt-1">{it.plan}</p>
                      </div>
                    ))}
                </div>
              </Card>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              {!!(pkg.inclusions || []).length && (
                <Card className="p-5">
                  <h2 className="font-bold text-ink-900 mb-3">Inclusions</h2>
                  <ul className="space-y-2">
                    {pkg.inclusions.map((inc, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-ink-700">
                        <FaCheckCircle className="text-emerald-500 mt-0.5 shrink-0" />
                        {inc}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
              {!!(pkg.exclusions || []).length && (
                <Card className="p-5">
                  <h2 className="font-bold text-ink-900 mb-3">Exclusions</h2>
                  <ul className="space-y-2">
                    {pkg.exclusions.map((exc, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-ink-700">
                        <FaTimesCircle className="text-red-400 mt-0.5 shrink-0" />
                        {exc}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>

            {!!(pkg.addOns || []).length && (
              <Card className="p-5 mb-6">
                <h2 className="font-bold text-ink-900 mb-3 flex items-center gap-2">
                  <FaPlusCircle className="text-brand-600" /> Optional add-ons
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {pkg.addOns.map((a, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between px-3 py-2 rounded-lg bg-ink-50 text-sm"
                    >
                      <span className="text-ink-700">{a.title}</span>
                      <span className="font-semibold text-brand-600">{formatINR(a.price)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {pkg.cancellationPolicy && (
              <Card className="p-5 mb-6">
                <h2 className="font-bold text-ink-900 mb-2">Cancellation policy</h2>
                <p className="text-sm text-ink-700 whitespace-pre-line">{pkg.cancellationPolicy}</p>
              </Card>
            )}

            <Card className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-ink-900">Reviews</h2>
                {partner.ratingCount > 0 && (
                  <StarDisplay value={partner.ratingAvg} count={partner.ratingCount} />
                )}
              </div>
              {reviews.length === 0 ? (
                <p className="text-sm text-ink-500">No reviews yet for this partner.</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((r, i) => (
                    <div key={r._id || i} className="pb-4 border-b border-ink-100 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-ink-800 text-sm">
                          {r.customerName || r.userName || "Rasutrip customer"}
                        </span>
                        <StarDisplay value={r.rating} size={12} />
                      </div>
                      <p className="text-sm text-ink-600">{r.text || r.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div>
            <Card className="p-5 sticky top-24">
              <p className="text-xs text-ink-400 uppercase font-semibold tracking-wide mb-1">
                Package price
              </p>
              <p className="text-3xl font-extrabold text-brand-600 mb-4">{formatINR(pkg.price)}</p>

              <Button variant="primary" size="lg" className="w-full mb-5" onClick={handleBuyNowClick}>
                Buy Now
              </Button>

              <div className="pt-4 border-t border-ink-100">
                <h3 className="text-sm font-bold text-ink-900 mb-2">Organized by</h3>
                <p className="font-semibold text-ink-800">{partner.businessName || "Rasutrip Partner"}</p>
                {partner.city && <p className="text-sm text-ink-500 mb-1">{partner.city}</p>}
                {partner.ratingCount > 0 && (
                  <div className="mb-2">
                    <StarDisplay value={partner.ratingAvg} count={partner.ratingCount} />
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <BookingModal
        show={showModal}
        onClose={() => !submitting && setShowModal(false)}
        mode="package"
        item={pkg}
        price={pkg.price}
        submitting={submitting}
        error={modalError}
        onSubmit={handleSubmitPurchase}
      />
    </div>
  );
};

export default PackageDetailPage;
